import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { spore } from '@/spore/runtime';
import { bindSimulatorKeys } from '@/spore/simulator';
import { getShowController } from '@/show/saysController';
import { createMycelium } from '@/show/mycelium';
import { useSporeSnapshot } from '@/hooks/useSporeSnapshot';
import { TitleScreen, HoldingScreen, PausedOverlay, HostNote } from '@/components/stage/StageOverlays';
import { FreeRevealStamp } from '@/components/stage/FreeHud';
import { UnlockOverlay } from '@/components/stage/LevelHud';
import { SaysAudienceHud, SaysHostHud, SaysCheatSheet } from '@/components/stage/SaysHud';
import { TouchTestOverlay } from '@/components/lab/TouchTestOverlay';
import { TrainingVideoOverlay } from '@/components/stage/TrainingVideoOverlay';
import { MemoryFragmentCard, FinaleRequest, SearchScreen, BroochReveal, StoryEndScreen, CornerControls } from '@/components/stage/StoryHud';
import { CalmGame } from '@/components/stage/CalmGame';

function useShowSnapshot(ctrl) {
  const [snap, setSnap] = useState(() => ctrl.getSnapshot());
  useEffect(() => {
    const update = () => setSnap(ctrl.getSnapshot());
    const unsub = ctrl.subscribe(update);
    const id = setInterval(update, 100);
    return () => {
      unsub();
      clearInterval(id);
    };
  }, [ctrl]);
  return snap;
}

export default function Stage() {
  const navigate = useNavigate();
  const ctrl = getShowController();
  const snap = useShowSnapshot(ctrl);
  const lab = useSporeSnapshot(4);
  const canvasRef = useRef(null);

  useEffect(() => {
    const v = createMycelium(canvasRef.current);
    ctrl.attachVisuals(v);
    return () => {
      ctrl.detachVisuals();
      v.destroy();
    };
  }, [ctrl]);

  useEffect(() => {
    const onKey = (e) => ctrl.handleKey(e, navigate);
    window.addEventListener('keydown', onKey);
    const unbindSim = bindSimulatorKeys(spore.sim);
    return () => {
      window.removeEventListener('keydown', onKey);
      unbindSim();
    };
  }, [ctrl, navigate]);

  const { ui } = snap;
  const story = ['search', 'brooch', 'mmmm', 'closing'].includes(snap.phase);
  const hud = !['landing', 'video', 'holding', 'end', 'calm'].includes(snap.phase) && !story;
  return (
    <main data-testid="show-stage" data-phase={snap.phase} className={`stage fixed inset-0 overflow-hidden bg-[#050807] ${snap.phase === 'video' ? 'cursor-none' : ''}`}>
      <canvas ref={canvasRef} data-testid="mycelium-canvas" className="absolute inset-0 h-full w-full" />
      <div className="stage-grid pointer-events-none absolute inset-0" />
      {(snap.phase === 'landing' || snap.phase === 'video') && <TitleScreen snap={snap} midiStatus={lab.status.status} onVideo={ctrl.playVideo} onStart={ctrl.startExperiment} />}
      {snap.phase === 'holding' && <HoldingScreen onStart={ctrl.startExperiment} />}
      {hud && <SaysAudienceHud snap={snap} />}
      {snap.phase === 'video' && <TrainingVideoOverlay video={ctrl.video} vs={snap.video} hud={ui.hostHud} onSkip={ctrl.skipVideo} />}
      {snap.phase === 'unlock' && <UnlockOverlay snap={snap} />}
      {snap.phase === 'analysis' && <FreeRevealStamp snap={snap} />}
      {snap.phase === 'memory' && <div data-testid="memory-scene-dim" className="memory-dim pointer-events-none absolute inset-0" />}
      {snap.phase === 'memory' && ['card', 'voice', 'hold', 'fadeout'].includes(snap.memStep) && <MemoryFragmentCard snap={snap} />}
      {snap.phase === 'finale' && <FinaleRequest />}
      {snap.phase === 'search' && <SearchScreen onFound={ctrl.broochFound} />}
      {['brooch', 'mmmm', 'closing'].includes(snap.phase) && <BroochReveal snap={snap} />}
      {snap.phase === 'calm' && <CalmGame snap={snap} onSoothe={ctrl.soothe} onYes={ctrl.calmYes} />}
      {snap.phase === 'end' && <StoryEndScreen snap={snap} />}
      {snap.paused && !snap.touchTest.running && <PausedOverlay />}
      {ui.hostHud && <SaysHostHud snap={snap} lab={lab} />}
      {!ui.hostHud && (snap.hostHint || ui.detectorWarning) && <span data-testid="host-prompt-dot" className="dot-live dot-pulse absolute bottom-2 left-2" style={{ background: 'var(--amber)' }} />}
      {!ui.hostHud && Object.values(snap.wordStatus).some((w) => !w.enabled) && <span data-testid="host-word-dot" className="dot-live absolute bottom-2 left-6" style={{ background: 'var(--amber)' }} />}
      {ui.cheatSheet && <SaysCheatSheet />}
      {ui.note && <HostNote text={ui.note} />}
      <TouchTestOverlay test={snap.touchTest} touching={snap.touching} hovering={snap.hovering} />
      <CornerControls armed={ui.resetArmed} onReset={ctrl.screenReset} onFullscreen={ctrl.toggleFullscreen} />
    </main>
  );
}
